import { updateStoreMembers } from '~/utils'
import { Userpilot } from 'userpilot'
import Store from '~/store'
import get from 'lodash/get'
import { Auth } from 'aws-amplify'
import AuthAPI from 'api/AuthAPI'
import UsersAPI from 'api/UsersAPI'
import PracticesAPI from 'api/PracticesAPI'
import FacilityServicesAPI from 'api/FacilityServicesAPI'
import SignupStepService from './SignupStepService'
import UserRolesEnum from '../enums/UserRolesEnum'
import SkipStepService from './SkipStepService'
import MessagesAPI from 'api/MessagesAPI'
import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
import PracticePlanEnum from '@/enums/PracticePlanEnum'
import InsurancePlanGroupsAPI from 'api/InsurancePlanGroupsAPI'

const TOKEN_REFRESH_SKEW_SECONDS = 60

let refreshPromise = null

function getJwtExp(token) {
    if (!token) return 0
    try {
        const payload = JSON.parse(atob(token.split('.')[1]))
        return Number(payload.exp) || 0
    } catch {
        return 0
    }
}

function isTokenExpiringSoon(token, skewSeconds = TOKEN_REFRESH_SKEW_SECONDS) {
    const exp = getJwtExp(token)
    if (!exp) return true
    return exp * 1000 <= Date.now() + skewSeconds * 1000
}

export default {
    setTokens(accessToken, idToken, refreshToken) {
        localStorage.setItem('accessToken', accessToken)
        localStorage.setItem('idToken', idToken)
        localStorage.setItem('refreshToken', refreshToken)
    },

    logIn(accessToken, idToken, refreshToken) {
        localStorage.removeItem('activeMember')
        this.setTokens(accessToken, idToken, refreshToken)
        localStorage.setItem('referralsCompleted', false)
    },

    setActiveMember({ id }) {
        localStorage.setItem('activeMember', id)
    },

    setActiveUserLocations(id) {
        PracticesAPI.getUserLocations(id).then((locations) => Store.dispatch('auth/setUserLocations', locations))
    },

    setLastRegisteredPhysician(physican) {
        Store.dispatch('auth/setLastRegisteredPhysician', physican)
    },

    getToken() {
        return localStorage.getItem('idToken')
    },

    /**
     * Sync Amplify Cognito session into localStorage.
     * Amplify refreshes access/id tokens via the refresh token when expired.
     */
    refreshTokens() {
        if (refreshPromise) {
            return refreshPromise
        }

        refreshPromise = Auth.currentSession()
            .then((session) => {
                const accessToken = session.getAccessToken().getJwtToken()
                const idToken = session.getIdToken().getJwtToken()
                const refreshToken = session.getRefreshToken().getToken()
                this.setTokens(accessToken, idToken, refreshToken)
                return idToken
            })
            .finally(() => {
                refreshPromise = null
            })

        return refreshPromise
    },

    /**
     * Returns a non-expired id token, refreshing via Cognito when needed.
     */
    getValidToken() {
        if (!this.isLoggedIn()) {
            return Promise.resolve(null)
        }

        const token = this.getToken()
        if (!isTokenExpiringSoon(token)) {
            return Promise.resolve(token)
        }

        return this.refreshTokens().catch((error) => {
            this.logOut()
            throw error
        })
    },

    loadSession(force) {
        if (!this.isLoggedIn()) {
            return Promise.resolve()
        }

        if (get(this.getUser(), 'id') && !force) {
            return Promise.resolve()
        }

        return this.getValidToken()
            .then((token) => {
                if (!token) {
                    return Promise.resolve()
                }
                return UsersAPI.getCurrent().then((user) => Store.dispatch('auth/setUser', user))
            })
            .then(() => {
                if (!this.isLoggedIn() || !get(this.getUser(), 'id')) {
                    return Promise.resolve()
                }

                if (this.isUserPracticeDemo()) {
                    Userpilot.identify(get(this.getUser(), 'id'), {
                        name: get(this.getUser(), 'firstName'),
                        email: get(this.getUser(), 'email'),
                    })
                }

                InsurancePlanGroupsAPI.getList().then((insurancePlanGroups) =>
                    Store.dispatch('auth/setInsurancePlans', insurancePlanGroups),
                )

                if (!this.getUser().practiceId) return false
                return Promise.resolve(
                    PracticesAPI.getMy().then((practice) => Store.dispatch('auth/setPractice', practice)),
                ).then(() => {
                    if (!this.getPractice() || !this.getPractice().id) {
                        return {}
                    }
                    const userId = this.isUserPhysician()
                        ? get(this.getUser(), 'id')
                        : localStorage.getItem('activeMember') || get(this.getUser(), 'id')
                    const practiceId = get(this.getPractice(), 'id')
                    if (userId) {
                        PracticesAPI.getUserLocations(userId).then((locations) =>
                            Store.dispatch('auth/setUserLocations', locations),
                        )
                        MessagesAPI.getUnread(
                            { toUserId: this.isUserPhysician() ? get(this.getUser(), 'id') : 0, toPracticeId: practiceId },
                            ['id'],
                        ).then((messages) => Store.dispatch('chat-messages/setUnread', messages))
                    }

                    if (this.getPractice().practiceType === PracticeTypeEnum.ANCILLARY) {
                        this.updateFacilityServicesByLocation()
                    }
                    return updateStoreMembers()
                })
            })
            .catch((error) => {
                // Refresh failed and session was cleared; allow route guards to send user to login
                if (!this.isLoggedIn()) {
                    return Promise.resolve()
                }
                return Promise.reject(error)
            })
    },

    logOut() {
        const token = this.getToken()
        localStorage.removeItem('accessToken')
        localStorage.removeItem('idToken')
        localStorage.removeItem('refreshToken')
        Store.dispatch('auth/clear')
        SignupStepService.resetAllShowSignupSteps()
        SkipStepService.resetAllShowSignupSteps()

        if (token) {
            return AuthAPI.logOut()
        }
    },

    isLoggedIn() {
        return Boolean(this.getToken())
    },

    isUserOnlyPhysician() {
        return [UserRolesEnum.PHYSICIAN.ID].includes(get(this.getUser(), 'userRole.id'))
    },

    isUserPhysician() {
        return [UserRolesEnum.PHYSICIAN.ID, UserRolesEnum.NURSE_PRACTITIONER.ID].includes(get(this.getUser(), 'userRole.id'))
    },

    isActiveMemberPhysician() {
        return [UserRolesEnum.PHYSICIAN.ID, UserRolesEnum.NURSE_PRACTITIONER.ID].includes(
            get(this.getActiveMember(), 'userRole.id'),
        )
    },

    isUserManager() {
        return get(this.getUser(), 'userRole.name') === UserRolesEnum.OFFICE_MANAGER.NAME
    },

    isUserAdministrator() {
        return get(this.getUser(), 'userRole.name') === UserRolesEnum.ADMINISTRATOR.NAME
    },

    isMasterUser() {
        return this.getUser().isMasterUser
    },

    isPracticeAncillary() {
        return this.getPractice().practiceType === PracticeTypeEnum.ANCILLARY
    },

    isPracticePremium() {
        return this.getPractice().subscription === PracticePlanEnum.PREMIUM
    },

    isPracticeTrial() {
        return this.getPractice().subscription === PracticePlanEnum.TRIAL_PERIOD
    },

    isPracticeOther() {
        return (
            this.getPractice().practiceType === PracticeTypeEnum.OTHER || this.getUser().practiceType === PracticeTypeEnum.OTHER
        )
    },

    isUserOther() {
        return [UserRolesEnum.PHYSICIAN.ID, UserRolesEnum.NURSE_PRACTITIONER.ID, UserRolesEnum.OFFICE_MANAGER.ID].includes(
            get(this.getUser(), 'userRole.id'),
        )
    },

    isUserTemporaryPhysician() {
        return [UserRolesEnum.TEMPORARY_PHYSICIAN.ID].includes(get(this.getUser(), 'userRole.id'))
    },

    isUserPracticeOther() {
        return (
            get(this.getUser(), 'practiceType') === PracticeTypeEnum.OTHER ||
            (this.getPractice() && this.getPractice().practiceType === PracticeTypeEnum.OTHER)
        )
    },

    isUserPracticeDemo() {
        const desiredValues = ['demo-physician', 'demo-ancillary', 'demo-primary-care']
        const filteredValues = Object.values(PracticeTypeEnum).filter((value) => desiredValues.includes(value))
        return filteredValues.includes(this.getUser()?.practiceType)
    },

    wasUserDemo() {
        return get(this.getUser(), 'wasDemo')
    },

    getActiveMember() {
        if (this.isUserPracticeOther()) {
            return this.getUser()
        }
        const memberId = localStorage.getItem('activeMember') || get(this.getUser(), 'id')
        return this.getMembers().find(({ id }) => parseInt(id) === parseInt(memberId))
    },

    getPracticeMember(memberId) {
        return this.getMembers().find(({ id }) => parseInt(id) === parseInt(memberId))
    },

    getUser() {
        return Store.getters['auth/getUser'] || {}
    },

    getPractice() {
        return Store.getters['auth/getPractice']
    },

    updatePractice(practice) {
        Store.dispatch('auth/setPractice', practice)
    },

    forceUpdatePractice() {
        return PracticesAPI.getMy().then((practice) => Store.dispatch('auth/setPractice', practice))
    },

    updateFacilityServicesByLocation() {
        return FacilityServicesAPI.getAncillaryServicesByLocation(this.getPractice().id).then((services) =>
            Store.dispatch('auth/setFacilityServicesByLocation', services),
        )
    },

    getMembers() {
        return Store.getters['auth/getMembers']
    },

    getLastRegisteredPhysician() {
        return Store.getters['auth/getLastRegisteredPhysician']
    },

    getUserLocations() {
        return Store.getters['auth/getUserLocations']
    },

    getFacilityServicesByLocation() {
        return Store.getters['auth/getFacilityServicesByLocation']
    },

    refreshPracticeMembers() {
        return updateStoreMembers()
    },

    hasUserHaveFindProviderSearch() {
        return (
            this.getUser().id === Number(localStorage.getItem('findProviderUserId')) && localStorage.getItem('findProviderQuery')
        )
    },

    checkPasswordOnBlur(password) {
        return UsersAPI.checkRegistrationPassword({ password })
    },
}
